import React, { useState } from "react";
import BasicModal from "../../ui/BasicModal/BasicModal";
import { Radio, Space, Input, Button, Row, Col, Form, notification } from "antd";
import useNotification from "../../base/hooks/useNotification";
import { httpService } from "../../base/services/httpService.service";
import AuthService from "../../base/services/authentication.service";
import {
   CancelOutcome,
   NO_SUBSCRIPTION_LEFT_MESSAGE,
   cancelThroughBilling,
   cancelsAtPeriodEnd,
   isBilledUntilScheduledEnd,
   isCancellationScheduled,
   isCommittedPastThisPeriod,
   scheduledEndOf
} from "../Billing/billingActions";
import { describeBillingError } from "../Billing/billingCodes";
import { formatBillingDate } from "../Billing/billingDates";
const { TextArea } = Input;

// On the new billing flow (`billingManaged`) the cancel goes through POST
// /Payment/cancel, and the dialog says when access ends, from the status held:
// a trial or a paid period runs to its end, anything else ends now. With the
// flow off it goes through cancelStripeSubscription, as it always did.
//
// An annual plan is billed monthly on a 12-month commitment. Cancelled before
// its last month, it stays active, and is billed monthly, until the commitment
// ends, on both flows, and the dialog names that date (`commitmentEnd`): from
// the status on the new flow, from the subscription read on the old one. The
// old flow's notice shows the server's answer once it is done.

// The reasons, by the code both cancel endpoints store them under.
const CANCEL_REASONS = [
   { code: 1, label: "I no longer use the service." },
   { code: 2, label: "I'm concerned about my privacy." },
   { code: 3, label: "I'm not satisfied with the service." },
   { code: 4, label: "I need to cut back on expenses." },
   { code: 5, label: "Other" }
];

// An active annual plan before its commitment's last month, on either flow.
const describeCommittedCancel = (commitmentEnd) =>
   `Your annual plan is a 12-month commitment, so it stays active and is billed monthly until ${formatBillingDate(
      commitmentEnd
   )}. It ends then, and won't renew.`;

// What the dialog says before anything is confirmed, on the new flow.
const describeWhenAccessEnds = (status) => {
   if (!cancelsAtPeriodEnd(status)) {
      return "Your access ends immediately, because nothing has been paid for the current period.";
   }
   if (isCommittedPastThisPeriod(status)) {
      return describeCommittedCancel(status.commitmentEnd);
   }
   const periodEnd = formatBillingDate(status?.currentPeriodEnd);
   return periodEnd
      ? `Your subscription stays active until ${periodEnd}, and won't renew.`
      : "Your subscription stays active until the end of the current period, and won't renew.";
};

// What the notice says once it is done, from the status the cancel answered with.
const describeCancellation = (status) => {
   if (!isCancellationScheduled(status)) {
      return "Your subscription has ended. Pick a plan whenever you want to start again.";
   }
   const endsAt = formatBillingDate(scheduledEndOf(status));
   if (isBilledUntilScheduledEnd(status)) {
      return `Your annual plan stays active, and is billed monthly, until ${endsAt}. It won't renew after that.`;
   }
   return endsAt
      ? `It stays active until ${endsAt}, and won't renew.`
      : "It stays active until the end of the current period, and won't renew.";
};

const CancelSubscription = ({
   isModalOpen,
   handleAccountModal,
   subscriptionId,
   handleCancelPlan,
   billingManaged = false,
   billingStatus,
   // The old flow only: when the commitment of an active annual plan before its
   // last month ends (isCommittedPastThisPeriod), null for anything else.
   commitmentEnd = null,
   // Say what the server's answer says once it is done (the billing page),
   // rather than the dialog's own sentence (the Subscription page).
   announceServerMessage = false,
   // A plan change waiting for the end of the contract, which cancelling drops.
   pendingPlanChange = null
}) => {
   const { openNotification } = useNotification();
   const [form] = Form.useForm();
   const [paymentMethodLoading, setPaymentMethodLoading] = useState(false);

   // The flow before the new one, as it was.
   const cancelThroughStripe = (values) => {
      setPaymentMethodLoading(true);
      const params = new URLSearchParams({
         subscriptionId: subscriptionId,
         cancelSubscriptionAfterPeriodEnds: true,
         reasonOfCancellation: values.reasonOfCancellation,
         customReasonOfCancellation: values.customReasonOfCancellation
      });
      httpService
         .post(`/Payment/cancelStripeSubscription?${params.toString()}`, {}, (response) => {
            if (!response) return;
            handleCancelPlan();
            // An annual plan's answer says until when it stays active.
            openNotification(
               "success",
               "Subscription canceled successfully",
               response.message || "Subscription successfully cancelled."
            );
         })
         .catch((error) => {
            openNotification("error", "", "Something went wrong. Please try again.");
         })
         .finally(() => {
            setPaymentMethodLoading(false);
            handleAccountModal();
         });
   };

   const cancelThroughNewFlow = async (values) => {
      setPaymentMethodLoading(true);

      try {
         const { outcome, status, message } = await cancelThroughBilling({
            organizationId: AuthService.getOrganizationId(),
            reasonOfCancellation: values.reasonOfCancellation,
            customReasonOfCancellation: values.customReasonOfCancellation
         });

         // The flag was switched off: the old cancel applies, and closes the
         // dialog. It needs the Stripe subscription the old screens know of;
         // without one (the billing page), the routes take the organization
         // back to those screens instead.
         if (outcome === CancelOutcome.FLOW_OFF && subscriptionId) {
            cancelThroughStripe(values);
            return;
         }

         if (outcome === CancelOutcome.NO_SUBSCRIPTION) {
            notification.warning({ message: "Heads up!", description: NO_SUBSCRIPTION_LEFT_MESSAGE });
         } else if (outcome === CancelOutcome.CANCELED) {
            notification.success({
               message: "Subscription canceled successfully",
               description: (announceServerMessage && message) || describeCancellation(status)
            });
         }
         handleCancelPlan();
      } catch (failure) {
         notification.error({
            message: "Oops! A problem has occurred!",
            description: describeBillingError(failure)
         });
      }

      setPaymentMethodLoading(false);
      handleAccountModal();
   };

   return (
      <BasicModal
         isModalOpen={isModalOpen}
         handleCancel={handleAccountModal}
         footerButtonCustomStyle
      >
         <Form onFinish={billingManaged ? cancelThroughNewFlow : cancelThroughStripe} form={form}>
            <div className="mt-5 mb-8">
               <h4>Cancel your subscription</h4>
               {billingManaged ? (
                  <p className="label-text mt-5">{describeWhenAccessEnds(billingStatus)}</p>
               ) : (
                  commitmentEnd && (
                     <p className="label-text mt-5">{describeCommittedCancel(commitmentEnd)}</p>
                  )
               )}
               {pendingPlanChange?.packetId && (
                  <p className="label-text mt-5">
                     The change to the {pendingPlanChange.packetName ?? "new"} plan you scheduled is
                     dropped too.
                  </p>
               )}
            </div>

            <>
               <p className="label-text mt-8 mb-5">
                  We're sorry to see you go! To help us improve, would you mind telling us why
                  you're canceling your subscription?
               </p>
               <Form.Item
                  name="reasonOfCancellation"
                  rules={[{ required: true, message: "Please select an option" }]}
               >
                  <Radio.Group>
                     <Space direction="vertical">
                        {CANCEL_REASONS.map(({ code, label }) => (
                           <Radio key={code} value={code}>
                              {label}
                           </Radio>
                        ))}
                     </Space>
                  </Radio.Group>
               </Form.Item>
               <>
                  <p className="label-text mt-8">
                     Is there anything we could have done to improve your experience? Please tell us
                     more about why you're canceling your subscription.
                  </p>
                  <Form.Item
                     name="customReasonOfCancellation"
                     rules={[{ required: true, message: "Please type something here" }]}
                  >
                     <TextArea rows={2} placeholder="Type something here" />
                  </Form.Item>
               </>
            </>
            <Row gutter={16} className="mt-8" justify="center" align="middle">
               <Col>
                  <Button
                     className="cancel"
                     onClick={handleAccountModal}
                     disabled={paymentMethodLoading}
                  >
                     Cancel
                  </Button>
               </Col>
               <Col>
                  <Button
                     htmlType="submit"
                     className="primaryButton"
                     loading={paymentMethodLoading}
                  >
                     Continue
                  </Button>
               </Col>
            </Row>
         </Form>
      </BasicModal>
   );
};

export default CancelSubscription;
